import { fetchAuthSession } from "aws-amplify/auth"
import {
  captureRequestEpoch,
  throwIfRequestObsolete,
} from "./authRequestLifecycle"
import {
  isNetworkError,
  MAX_NETWORK_RETRIES,
  OfflineError,
  reportNetworkFailure,
  throwIfAborted,
  waitUntilOnline,
} from "./connectivity"

interface GetAccessTokenOptions {
  /** The session the request belongs to (see authRequestLifecycle). Defaults
   *  to the current one; the axios client passes the epoch its request was
   *  created in, so a resent request keeps its original session. */
  epoch?: number
  /** Cancels the read, and any wait for the network, with the caller's
   *  AbortError: the chat queue relies on it to force the next message out. */
  signal?: AbortSignal | null
}

/**
 * The bearer token for every API client. Waits instead of failing while the
 * network is down: a tab reopened after a night asleep has an expired access
 * token, and its refresh goes out before the network is back. Throws only
 * when the user is really signed out, or the network did not come back.
 *
 * A wait can outlive the session: sign-out, then someone else signing in,
 * in the same tab. After every wait and every read the request's session is
 * checked, and a request whose session has ended fails with an AbortError
 * instead of picking up the next user's token.
 */
export async function getAccessToken({
  epoch = captureRequestEpoch(),
  signal,
}: GetAccessTokenOptions = {}): Promise<string> {
  const ensureStillWanted = () => {
    throwIfAborted(signal)
    throwIfRequestObsolete(epoch)
  }
  for (let retries = 0; ; retries++) {
    // Known offline: wait here rather than send a refresh that cannot arrive.
    const online = await waitUntilOnline({ signal })
    ensureStillWanted()
    if (!online) throw new OfflineError()
    try {
      let token = (await fetchAuthSession()).tokens?.accessToken.toString()
      ensureStillWanted()
      if (!token) {
        // A request can race the session being hydrated (a page opened
        // straight after sign-in, or a token refreshing mid-flight): the
        // first read returns nothing, and going straight to an error toast
        // produced four "Token is undefined" toasts in seven seconds (A6,
        // 2026-09-22). One forced refresh settles it; only if THAT is also
        // empty is the user really signed out.
        token = (
          await fetchAuthSession({ forceRefresh: true })
        ).tokens?.accessToken.toString()
        ensureStillWanted()
      }
      if (!token) {
        throw Error("No access token after refresh")
      }
      return token
    } catch (error) {
      if (!isNetworkError(error)) throw error
      // The refresh never reached Cognito. Amplify keeps the tokens and the
      // user stays signed in, so this is no reason to fail the request yet.
      reportNetworkFailure()
      const backOnline =
        retries < MAX_NETWORK_RETRIES && (await waitUntilOnline({ signal }))
      ensureStillWanted()
      if (!backOnline) throw error
    }
  }
}
